import React, { useMemo, useCallback } from "react";
import EmptyComponent from "./EmptyComponent";
import { useSelector, shallowEqual } from "react-redux";
import { RiRadioButtonLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";

const WeatherStations = () => {
  const navigate = useNavigate();

  const navigateToSataHistory = useCallback(
    (slug) => {
      navigate(`/SATA-history/${slug}`);
    },
    [navigate]
  );

  // Selectors for data and loading/error states
  const weather = useSelector((state) => state.weather);
  const weatherLoading = weather.weatherLoading;
  const weatherData = weather.weatherData.SATA2 || [];
  const weatherError = weather.weatherError;
  const information = useSelector((state) => state.information);
  const informationData = information.informationData;
  const informationLoading = information.informationLoading;
  const informationError = information.informationError;
  const login = useSelector((state) => state.login);
  const isCompany = login.isCompany;
  const isLoggedIn = login.accessToken;

  const modifiedWeatherData = useMemo(() => {
    return weatherData.map((station) => {
      const specificStationInformation = informationData.find(
        (specificStation) =>
          specificStation.device === "SATA2" &&
          specificStation.device_id === station.device_id
      );
      return { ...station, ...specificStationInformation };
    });
  }, [weatherData, informationData]);

  // Helper functions
  const PersianSortComparer = useCallback((a, b) => {
    if (a.province === b.province) {
      if (a.city === b.city) return a.stationName.localeCompare(b.stationName);
      return a.city.localeCompare(b.city);
    }
    return a.province.localeCompare(b.province);
  }, []);

  const convertToJalali = useCallback((datetime) => {
    const A = datetime.slice(0, 10);
    const B = datetime.slice(11, 19);
    const C = new Date(A);
    const D = new Intl.DateTimeFormat("fa-IR").format(C);
    return `${D} (${B})`;
  }, []);

  const checkOnlineOrOffline = useCallback((datetime) => {
    const diff = new Date() - new Date(datetime);
    return diff < 30 * 60 * 1000; // last 30 minutes
  }, []);

  // Preprocess and sort stations data
  const processedStationsData = useMemo(() => {
    if (!Array.isArray(weatherData) || !Array.isArray(informationData))
      return [];

    return weatherData
      .map((station) => {
        const info = informationData.find(
          (s) => s.device === "SATA2" && s.device_id === station.device_id
        );
        return {
          ...station,
          ...info,
          isOnline: checkOnlineOrOffline(station.ccreated_datetime),
          jalaliDate: convertToJalali(station.ccreated_datetime),
        };
      })
      .filter((station) => !station.is_test)
      .sort(PersianSortComparer);
  }, [
    weatherData,
    informationData,
    PersianSortComparer,
    checkOnlineOrOffline,
    convertToJalali,
  ]);

  return (
    <div id="stationTable" className="my-5">
      <div className="container my-5">
        <div className="table-responsive">
          <table className="table table-striped table-bordered table-hover text-center align-middle">
            <thead className="table-light">
              <tr>
                {isCompany && (
                  <>
                    <th className="px-3">وضعیت</th>
                    <th className="px-3">سریال</th>
                    <th className="px-3">دستگاه</th>
                  </>
                )}
                <th className="d-none d-md-table-cell px-3">استان</th>
                <th className="d-none d-md-table-cell px-3">شهر</th>
                <th className="px-3">نام ایستگاه</th>
                {isCompany && <th className="px-3">آخرین داده</th>}
                {isLoggedIn && <th className="px-3">عملیات</th>}
              </tr>
            </thead>
            <tbody>
              {processedStationsData.map((station) => (
                <tr key={station.slug}>
                  {isCompany && (
                    <td
                      className={`px-3 ${
                        station.isOnline ? "text-success" : "text-danger"
                      }`}
                    >
                      <RiRadioButtonLine />
                    </td>
                  )}
                  {isCompany && (
                    <>
                      <td className="px-3">{station.device_id}</td>
                      <td className="px-3">{station.device}</td>
                    </>
                  )}
                  <td className="d-none d-md-table-cell px-3">
                    {station.province}
                  </td>
                  <td className="d-none d-md-table-cell px-3">
                    {station.city}
                  </td>
                  <td className="px-3">{station.stationName}</td>
                  {isCompany && <td className="px-3">{station.jalaliDate}</td>}
                  {isLoggedIn && (
                    <td className="px-3">
                      <button
                        className="btn btn-dark btn-sm"
                        onClick={() => navigateToSataHistory(station.slug)}
                      >
                        اطلاعات ایستگاه
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WeatherStations;
